import type {
  CatalogModel,
  CatalogResponse,
  CatalogSku,
  CatalogUnitPricing,
  UnitUsageUnit,
} from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import { PlatformApiError } from '../api/client.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useCatalog } from '../api/useCatalog.ts';
import { PanelError } from '../components/PanelError.tsx';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { formatCnyRateFromUsdRate, formatUsdMicrosAsCny } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { trimCnyTrailingZeros } from './trimCnyTrailingZeros.ts';
import {
  Chip,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/billing/pricing` (+ `/pricing` alias) — the model price surface.
 * Read-only leaf.
 *
 * Epic #898 P6 (#912): when the catalog carries the additive `skus[]`
 * market shape, the page renders it grouped by namespace — namespace
 * header (display name + slug), one row per SKU with its effective
 * per-dimension prices (base price × rate_multiplier) and a discount
 * marker when the org multiplier undercuts the platform base. When
 * `skus` is absent or empty (pre-#912 platform), the page falls back to
 * the legacy vendor-grouped model × channel matrices (epic #897 PR 9);
 * the fallback goes away with #916.
 *
 * Legacy filters are `ui/Select`s (vendor, channel) plus a search
 * input; the market view swaps them for a namespace `Select` + search.
 * All auto-apply. Rates render as CNY via the configured display rate;
 * a discount shows the struck-through base rate under the effective
 * one in both views.
 */

type TranslateFn = ReturnType<typeof useT>;

interface MatrixChannel {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
}

interface VendorMatrix {
  readonly vendor: string;
  readonly displayName: string;
  readonly channels: readonly MatrixChannel[];
  readonly models: readonly CatalogModel[];
}

interface NamespaceGroup {
  readonly namespaceId: string;
  readonly slug: string;
  readonly displayName: string;
  readonly skus: readonly CatalogSku[];
}

/**
 * The flat per-dimension pricing fields shared verbatim by
 * `CatalogModel` and `CatalogSku` — lets one line builder serve both
 * the legacy matrices and the SKU market.
 */
type PricingFields = Pick<
  CatalogModel,
  | 'vendor'
  | 'input_per_mtok_usd'
  | 'output_per_mtok_usd'
  | 'cache_read_per_mtok_usd'
  | 'cache_write_per_mtok_usd'
  | 'cache_write_5m_per_mtok_usd'
  | 'cache_write_1h_per_mtok_usd'
>;

const ALL_FILTER_VALUE = 'all';

export function Pricing(): JSX.Element {
  const t = useT();
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();
  const skuGroups = useMemo(() => buildNamespaceGroups(catalogQ.data), [catalogQ.data]);
  const matrices = useMemo(() => buildVendorMatrices(catalogQ.data), [catalogQ.data]);
  const channelOptions = useMemo(() => collectChannelOptions(matrices), [matrices]);
  const [vendorFilter, setVendorFilter] = useState<string>(ALL_FILTER_VALUE);
  const [channelFilter, setChannelFilter] = useState<string>(ALL_FILTER_VALUE);
  const [query, setQuery] = useState('');
  const visibleMatrices = useMemo(
    () => filterVendorMatrices(matrices, vendorFilter, channelFilter, query),
    [channelFilter, matrices, query, vendorFilter],
  );
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  return (
    <section data-testid="pricing-page" className="flex flex-col gap-4">
      <PageHeader title={t('pricing.title')} sub={t('pricing.subtitle')} testId="pricing-header" />

      {catalogQ.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="pricing-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
          <span className="sr-only">{t('pricing.loading')}</span>
        </div>
      ) : catalogQ.isError ? (
        <PanelError
          message={
            catalogQ.error instanceof PlatformApiError
              ? `${catalogQ.error.code}: ${catalogQ.error.message}`
              : coerceErrorMessage(catalogQ.error, t('pricing.error'))
          }
          retryLabel={t('pricing.retry')}
          onRetry={() => {
            void catalogQ.refetch();
          }}
          testId="pricing-error"
        />
      ) : skuGroups.length > 0 ? (
        <SkuMarket groups={skuGroups} cnyPerUsd={cnyPerUsd} />
      ) : matrices.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState testId="pricing-empty" title={t('pricing.empty')} />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <Select
              value={vendorFilter}
              onValueChange={setVendorFilter}
              options={[
                { value: ALL_FILTER_VALUE, label: t('pricing.filter.all_vendors') },
                ...matrices.map((matrix) => ({
                  value: matrix.vendor,
                  label: matrix.displayName,
                })),
              ]}
              ariaLabel={t('pricing.filter.vendor_label')}
              className="lg:w-52"
              testId="pricing-filter-vendor"
            />
            <Select
              value={channelFilter}
              onValueChange={setChannelFilter}
              options={[
                { value: ALL_FILTER_VALUE, label: t('pricing.filter.all_channels') },
                ...channelOptions.map((channel) => ({
                  value: channel.id,
                  label: (
                    <span className="inline-flex items-baseline gap-1.5">
                      {channel.displayName}
                      <span className="font-mono text-micro text-paper-low">
                        ×{formatRateMultiplier(channel.rateMultiplier)}
                      </span>
                    </span>
                  ),
                })),
              ]}
              ariaLabel={t('pricing.filter.channel_label')}
              className="lg:w-64"
              testId="pricing-filter-channel"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t('pricing.search.placeholder')}
              aria-label={t('pricing.search.placeholder')}
              className="lg:ml-auto lg:w-72"
              data-testid="pricing-search"
            />
          </div>

          {visibleMatrices.length === 0 ? (
            <div className="rounded-lg border border-line-soft bg-card">
              <EmptyState testId="pricing-no-results" title={t('pricing.no_results')} />
            </div>
          ) : (
            visibleMatrices.map((matrix) => (
              <VendorPricingSection key={matrix.vendor} matrix={matrix} cnyPerUsd={cnyPerUsd} />
            ))
          )}
        </>
      )}
    </section>
  );
}

function SkuMarket({
  groups,
  cnyPerUsd,
}: {
  readonly groups: readonly NamespaceGroup[];
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const [namespaceFilter, setNamespaceFilter] = useState<string>(ALL_FILTER_VALUE);
  const [query, setQuery] = useState('');
  const visibleGroups = useMemo(
    () => filterNamespaceGroups(groups, namespaceFilter, query),
    [groups, namespaceFilter, query],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="pricing-sku-market">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <Select
          value={namespaceFilter}
          onValueChange={setNamespaceFilter}
          options={[
            { value: ALL_FILTER_VALUE, label: t('pricing.filter.all_namespaces') },
            ...groups.map((group) => ({
              value: group.namespaceId,
              label: group.displayName,
            })),
          ]}
          ariaLabel={t('pricing.filter.namespace_label')}
          className="lg:w-64"
          testId="pricing-filter-namespace"
        />
        <Input
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder={t('pricing.search.placeholder')}
          aria-label={t('pricing.search.placeholder')}
          className="lg:ml-auto lg:w-72"
          data-testid="pricing-search"
        />
      </div>

      {visibleGroups.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState testId="pricing-no-results" title={t('pricing.no_results')} />
        </div>
      ) : (
        visibleGroups.map((group) => (
          <NamespaceSection key={group.namespaceId} group={group} cnyPerUsd={cnyPerUsd} />
        ))
      )}
    </div>
  );
}

function NamespaceSection({
  group,
  cnyPerUsd,
}: {
  readonly group: NamespaceGroup;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <section data-testid={`pricing-namespace-${group.slug}`} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="m-0 text-section text-paper">{group.displayName}</h2>
        <span className="font-mono text-micro text-paper-low">{group.slug}</span>
        <span className="font-mono text-micro text-paper-low">
          {t('pricing.namespace.summary', { skus: group.skus.length })}
        </span>
        {/* A group with unit-metered rows cannot honestly claim the per-Mtok
            dimension for the whole table — its badge goes neutral (#1706);
            each unit line already names its own dimension (每张/Per image). */}
        <span className="ml-auto rounded border border-line-soft bg-card px-2 py-0.5 font-mono text-micro text-paper-low">
          {t(
            group.skus.some((sku) => sku.unit_pricing !== undefined)
              ? 'pricing.unit.neutral'
              : 'pricing.unit',
          )}
        </span>
      </div>

      <Table>
        <THead>
          <Tr interactive={false}>
            <Th className="w-[280px] min-w-[240px]">{t('pricing.column.model')}</Th>
            <Th className="min-w-[160px]">{t('pricing.column.rate')}</Th>
            <Th className="min-w-[320px] border-l">{t('pricing.column.prices')}</Th>
          </Tr>
        </THead>
        <TBody>
          {group.skus.map((sku) => (
            <Tr key={sku.sku_id} data-testid={`pricing-sku-${sku.sku_id}`}>
              <Td heading className="align-top">
                <span className="flex max-w-[260px] flex-col gap-1 max-md:max-w-none">
                  <span className="break-all font-mono text-dense font-medium text-paper">
                    {sku.sku_name}
                  </span>
                  {sku.model_display_name !== null ? (
                    <span className="text-label text-paper-low">{sku.model_display_name}</span>
                  ) : null}
                </span>
              </Td>
              <Td label={t('pricing.column.rate')} className="align-top">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded bg-signal-dim px-1.5 py-0.5 font-mono text-micro text-signal">
                    ×{formatRateMultiplier(sku.rate_multiplier)}
                  </span>
                  {hasRateDiscount(sku.rate_multiplier, sku.base_rate_multiplier) ? (
                    <>
                      <span className="font-mono text-micro text-paper-low line-through">
                        ×{formatRateMultiplier(sku.base_rate_multiplier)}
                      </span>
                      <Chip tone="ok" lamp={false} testId={`pricing-sku-discount-${sku.sku_id}`}>
                        {t('pricing.sku.discount')}
                      </Chip>
                    </>
                  ) : null}
                </span>
              </Td>
              <Td
                label={t('pricing.column.prices')}
                stack
                className="border-l align-top max-md:border-l-0"
                data-testid={`pricing-sku-prices-${sku.sku_id}`}
              >
                {sku.unit_pricing !== undefined ? (
                  // Unit-metered SKU (#1706): per-unit prices from the
                  // current model_unit_pricing generation. The token
                  // dimensions are the zero-rate anchor generation and are
                  // deliberately not rendered.
                  <UnitPricingCell
                    unitPricing={sku.unit_pricing}
                    rateMultiplier={sku.rate_multiplier}
                    baseRateMultiplier={sku.base_rate_multiplier}
                    cnyPerUsd={cnyPerUsd}
                  />
                ) : (
                  <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3 xl:grid-cols-5">
                    {pricingLinesForModel(sku).map((line) => (
                      <PriceLine
                        key={line.labelKey}
                        label={t(line.labelKey)}
                        value={line.value}
                        notBilled={line.notBilled}
                        notBilledLabel={t('pricing.rate.not_billed')}
                        rateMultiplier={sku.rate_multiplier}
                        baseRateMultiplier={sku.base_rate_multiplier}
                        cnyPerUsd={cnyPerUsd}
                      />
                    ))}
                  </dl>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </section>
  );
}

function VendorPricingSection({
  matrix,
  cnyPerUsd,
}: {
  readonly matrix: VendorMatrix;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <section data-testid={`pricing-vendor-${matrix.vendor}`} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="m-0 text-section text-paper">{matrix.displayName}</h2>
        <span className="font-mono text-micro text-paper-low">
          {t('pricing.vendor.summary', {
            models: matrix.models.length,
            channels: matrix.channels.length,
          })}
        </span>
        {/* Same neutral-badge rule as the SKU market (#1706). */}
        <span className="ml-auto rounded border border-line-soft bg-card px-2 py-0.5 font-mono text-micro text-paper-low">
          {t(
            matrix.models.some((model) => model.unit_pricing !== undefined)
              ? 'pricing.unit.neutral'
              : 'pricing.unit',
          )}
        </span>
      </div>

      {matrix.channels.length === 0 ? (
        <p
          className="m-0 rounded-lg border border-line-soft bg-card px-4 py-5 text-dense text-paper-low"
          data-testid={`pricing-vendor-${matrix.vendor}-empty`}
        >
          {t('pricing.vendor.no_channels')}
        </p>
      ) : (
        <Table>
          <THead>
            <Tr interactive={false}>
              <Th className="w-[280px] min-w-[240px]">{t('pricing.column.model')}</Th>
              {matrix.channels.map((channel) => (
                <Th key={channel.id} className="min-w-[220px] border-l">
                  <span className="flex flex-col gap-1.5 py-1">
                    <span className="text-dense font-medium text-paper">{channel.displayName}</span>
                    <span className="font-mono text-micro font-normal text-paper-low">
                      {channel.slug}
                    </span>
                    {channel.description != null ? (
                      <span className="max-w-[220px] whitespace-normal text-micro font-normal leading-4 text-paper-low">
                        {channel.description}
                      </span>
                    ) : null}
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="rounded bg-signal-dim px-1.5 py-0.5 font-mono text-micro text-signal">
                        ×{formatRateMultiplier(channel.rateMultiplier)}
                      </span>
                      {hasDiscount(channel) ? (
                        <span className="font-mono text-micro font-normal text-paper-low line-through">
                          ×{formatRateMultiplier(channel.baseRateMultiplier)}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody>
            {matrix.models.map((model) => (
              <Tr key={model.model}>
                <Td heading className="align-top">
                  <span className="flex max-w-[260px] flex-col gap-1 max-md:max-w-none">
                    <span className="break-all font-mono text-dense font-medium text-paper">
                      {model.model}
                    </span>
                    {model.display_name !== null ? (
                      <span className="text-label text-paper-low">{model.display_name}</span>
                    ) : null}
                  </span>
                </Td>
                {matrix.channels.map((channel) => (
                  <Td
                    key={`${model.model}::${channel.id}`}
                    label={channel.displayName}
                    stack
                    className="border-l align-top max-md:border-l-0"
                    data-testid={`pricing-cell-${matrix.vendor}-${model.model}-${channel.slug}`}
                  >
                    <PriceCell model={model} channel={channel} cnyPerUsd={cnyPerUsd} t={t} />
                  </Td>
                ))}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

function PriceCell({
  model,
  channel,
  cnyPerUsd,
  t,
}: {
  readonly model: CatalogModel;
  readonly channel: MatrixChannel;
  readonly cnyPerUsd: number | null;
  readonly t: TranslateFn;
}): JSX.Element {
  const covered = model.channels.some((c) => c.id === channel.id);
  if (!covered) {
    return (
      <span className="inline-flex min-h-[96px] w-full items-center justify-center rounded border border-dashed border-line text-label text-paper-low">
        {t('pricing.cell.unavailable')}
      </span>
    );
  }
  if (model.unit_pricing !== undefined) {
    // Unit-metered model (#1706): the legacy matrix cell renders per-unit
    // prices with the channel's multiplier chain; the zero-rate token
    // anchor dimensions are deliberately not rendered.
    return (
      <UnitPricingCell
        unitPricing={model.unit_pricing}
        rateMultiplier={channel.rateMultiplier}
        baseRateMultiplier={channel.baseRateMultiplier}
        cnyPerUsd={cnyPerUsd}
      />
    );
  }
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
      {pricingLinesForModel(model).map((line) => (
        <PriceLine
          key={line.labelKey}
          label={t(line.labelKey)}
          value={line.value}
          notBilled={line.notBilled}
          notBilledLabel={t('pricing.rate.not_billed')}
          rateMultiplier={channel.rateMultiplier}
          baseRateMultiplier={channel.baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </dl>
  );
}

/**
 * The prices cell of a unit-metered row (#1706). Empty rates means the model
 * is unit-metered but no price generation is currently effective: render
 * "pricing pending" — never the zero token dimensions, and never ¥0.00.
 */
function UnitPricingCell({
  unitPricing,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly unitPricing: CatalogUnitPricing;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  if (unitPricing.rates.length === 0) {
    return (
      <p className="m-0 text-dense text-paper-low" data-testid="pricing-unit-pending">
        {t('pricing.rate.unit.pending')}
      </p>
    );
  }
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
      {unitPricing.rates.map((rate) => (
        <UnitPriceLine
          key={`${rate.unit}:${rate.variant_key}`}
          label={unitRateLabel(t, rate.unit, rate.variant_key)}
          rateUsdMicros={rate.rate_usd_micros_per_unit}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </dl>
  );
}

/**
 * One per-unit price line. Unit prices are integer micro-USD and can sit far
 * below one cent (a per-character TTS rate is tens of micros), so they render
 * through the micros-precision CNY channel the Requests page uses — the
 * 2-decimal per-Mtok formatter would show ¥0.00. The multiplied USD amount
 * is passed through un-rounded: the ONE rounding happens on the CNY side
 * inside the formatter, so a 1-micro rate under a deep discount still shows
 * ¥0.000001 instead of collapsing to ¥0.000000 at an intermediate step.
 */
function UnitPriceLine({
  label,
  rateUsdMicros,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly label: string;
  readonly rateUsdMicros: number;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const display = trimCnyTrailingZeros(
    formatUsdMicrosAsCny(rateUsdMicros * rateMultiplier, cnyPerUsd),
  );
  const original = hasRateDiscount(rateMultiplier, baseRateMultiplier)
    ? trimCnyTrailingZeros(formatUsdMicrosAsCny(rateUsdMicros * baseRateMultiplier, cnyPerUsd))
    : null;
  return (
    <div className="min-w-0">
      <dt className="mb-1 truncate text-micro text-paper-low">{label}</dt>
      <dd className="m-0 flex flex-col gap-0.5">
        <span className="font-mono text-dense font-medium text-paper">{display}</span>
        {original !== null ? (
          <span className="font-mono text-micro text-paper-low line-through">{original}</span>
        ) : null}
      </dd>
    </div>
  );
}

function PriceLine({
  label,
  value,
  notBilled = false,
  notBilledLabel,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly notBilled?: boolean;
  readonly notBilledLabel: string;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const display = notBilled
    ? notBilledLabel
    : value === null
      ? '—'
      : trimCnyTrailingZeros(formatCnyRateFromUsdRate(value * rateMultiplier, cnyPerUsd));
  const original =
    !notBilled && value !== null && hasRateDiscount(rateMultiplier, baseRateMultiplier)
      ? trimCnyTrailingZeros(formatCnyRateFromUsdRate(value * baseRateMultiplier, cnyPerUsd))
      : null;
  return (
    <div className="min-w-0">
      <dt className="mb-1 truncate text-micro text-paper-low">{label}</dt>
      <dd className="m-0 flex flex-col gap-0.5">
        <span className="font-mono text-dense font-medium text-paper">{display}</span>
        {original !== null ? (
          <span className="font-mono text-micro text-paper-low line-through">{original}</span>
        ) : null}
      </dd>
    </div>
  );
}

function compareChannelsByPrice(a: MatrixChannel, b: MatrixChannel): number {
  const priceDelta = a.rateMultiplier - b.rateMultiplier;
  if (priceDelta !== 0) return priceDelta;
  return a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id);
}

function hasRateDiscount(rateMultiplier: number, baseRateMultiplier: number): boolean {
  return baseRateMultiplier > rateMultiplier + 0.0000001;
}

function hasDiscount(channel: MatrixChannel): boolean {
  return hasRateDiscount(channel.rateMultiplier, channel.baseRateMultiplier);
}

function isAnthropicVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'anthropic';
}

function isOpenAiVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'openai';
}

function formatRateMultiplier(value: number): string {
  return value.toFixed(4);
}

/**
 * Per-unit price label for a unit-metered SKU line (#1706). The unit noun
 * dictionary is deliberately its own key set (`pricing.rate.unit.*`): the
 * `requests.unit_billing.unit.*` words are count nouns ("3 张"), not the
 * "每张" per-unit form a price line needs. A non-default variant rides as a
 * verbatim suffix — variant keys are operator-defined identifiers.
 */
function unitRateLabelKey(unit: UnitUsageUnit): I18nKey {
  switch (unit) {
    case 'call':
      return 'pricing.rate.unit.call';
    case 'second':
      return 'pricing.rate.unit.second';
    case 'character':
      return 'pricing.rate.unit.character';
    case 'image':
      return 'pricing.rate.unit.image';
  }
}

function unitRateLabel(t: TranslateFn, unit: UnitUsageUnit, variantKey: string): string {
  const base = t(unitRateLabelKey(unit));
  return variantKey === '' ? base : `${base} · ${variantKey}`;
}

function pricingLinesForModel(model: PricingFields): ReadonlyArray<{
  labelKey: I18nKey;
  value: number | null;
  notBilled?: boolean;
}> {
  const common = [
    { labelKey: 'pricing.rate.input', value: model.input_per_mtok_usd },
    { labelKey: 'pricing.rate.output', value: model.output_per_mtok_usd },
    { labelKey: 'pricing.rate.cache_read', value: model.cache_read_per_mtok_usd },
  ] satisfies ReadonlyArray<{ labelKey: I18nKey; value: number | null }>;
  if (isAnthropicVendor(model.vendor)) {
    return [
      ...common,
      {
        labelKey: 'pricing.rate.cache_write_5m',
        value: model.cache_write_5m_per_mtok_usd ?? model.cache_write_per_mtok_usd,
      },
      {
        labelKey: 'pricing.rate.cache_write_1h',
        value: model.cache_write_1h_per_mtok_usd ?? null,
      },
    ];
  }
  if (isOpenAiVendor(model.vendor)) {
    return [
      { labelKey: 'pricing.rate.input', value: model.input_per_mtok_usd },
      { labelKey: 'pricing.rate.cache_read', value: model.cache_read_per_mtok_usd },
      {
        labelKey: 'pricing.rate.cache_write',
        value: model.cache_write_per_mtok_usd,
        notBilled: (model.cache_write_per_mtok_usd ?? 0) <= 0,
      },
      { labelKey: 'pricing.rate.output', value: model.output_per_mtok_usd },
    ];
  }
  return [
    ...common,
    { labelKey: 'pricing.rate.cache_write', value: model.cache_write_per_mtok_usd },
  ];
}

function skuMatchesQuery(sku: CatalogSku, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return true;
  return [
    sku.sku_name,
    sku.model,
    sku.model_display_name,
    sku.vendor,
    sku.namespace_slug,
    sku.namespace_display_name,
  ]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(trimmed));
}

function filterNamespaceGroups(
  groups: readonly NamespaceGroup[],
  namespaceFilter: string,
  query: string,
): readonly NamespaceGroup[] {
  return groups
    .filter(
      (group) => namespaceFilter === ALL_FILTER_VALUE || group.namespaceId === namespaceFilter,
    )
    .map((group) => ({
      ...group,
      skus: group.skus.filter((sku) => skuMatchesQuery(sku, query)),
    }))
    .filter((group) => group.skus.length > 0);
}

function buildNamespaceGroups(catalog: CatalogResponse | undefined): readonly NamespaceGroup[] {
  const skus = catalog?.skus;
  if (skus === undefined || skus.length === 0) return [];
  const groups = new Map<
    string,
    {
      readonly namespaceId: string;
      readonly slug: string;
      readonly displayName: string;
      readonly skus: CatalogSku[];
    }
  >();
  for (const sku of skus) {
    const existing = groups.get(sku.namespace_id);
    if (existing === undefined) {
      groups.set(sku.namespace_id, {
        namespaceId: sku.namespace_id,
        slug: sku.namespace_slug,
        displayName: sku.namespace_display_name,
        skus: [sku],
      });
    } else {
      existing.skus.push(sku);
    }
  }
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      skus: [...group.skus].sort((a, b) => a.sku_name.localeCompare(b.sku_name)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName) || a.slug.localeCompare(b.slug));
}

function modelMatchesQuery(model: CatalogModel, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return true;
  return [model.model, model.display_name, model.family, model.vendor]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(trimmed));
}

function filterVendorMatrices(
  matrices: readonly VendorMatrix[],
  vendorFilter: string,
  channelFilter: string,
  query: string,
): readonly VendorMatrix[] {
  return matrices
    .filter((matrix) => vendorFilter === ALL_FILTER_VALUE || matrix.vendor === vendorFilter)
    .map((matrix) => {
      const channels =
        channelFilter === ALL_FILTER_VALUE
          ? matrix.channels
          : matrix.channels.filter((channel) => channel.id === channelFilter);
      const models = matrix.models.filter((model) => {
        if (!modelMatchesQuery(model, query)) return false;
        if (channelFilter === ALL_FILTER_VALUE) return true;
        return model.channels.some((channel) => channel.id === channelFilter);
      });
      return { ...matrix, channels, models };
    })
    .filter((matrix) => matrix.channels.length > 0 && matrix.models.length > 0);
}

function collectChannelOptions(matrices: readonly VendorMatrix[]): readonly MatrixChannel[] {
  const channels = new Map<string, MatrixChannel>();
  for (const matrix of matrices) {
    for (const channel of matrix.channels) {
      if (!channels.has(channel.id)) channels.set(channel.id, channel);
    }
  }
  return Array.from(channels.values()).sort(compareChannelsByPrice);
}

function buildVendorMatrices(catalog: CatalogResponse | undefined): readonly VendorMatrix[] {
  if (catalog === undefined) return [];
  const vendorNames = new Map(catalog.vendors.map((v) => [v.slug, v.display_name]));
  const vendors = new Set<string>();
  for (const vendor of catalog.vendors) vendors.add(vendor.slug);
  for (const model of catalog.models) vendors.add(model.vendor);

  return Array.from(vendors)
    .sort((a, b) => a.localeCompare(b))
    .map((vendor) => {
      const models = catalog.models
        .filter((m) => m.vendor === vendor)
        .sort((a, b) => a.model.localeCompare(b.model));
      const channels = new Map<string, MatrixChannel>();
      for (const model of models) {
        for (const channel of model.channels) {
          if (!channels.has(channel.id)) {
            channels.set(channel.id, {
              id: channel.id,
              slug: channel.slug,
              displayName: channel.display_name,
              description: channel.description ?? null,
              rateMultiplier: channel.rate_multiplier,
              baseRateMultiplier: channel.base_rate_multiplier ?? channel.rate_multiplier,
            });
          }
        }
      }
      return {
        vendor,
        displayName: vendorNames.get(vendor) ?? vendor,
        channels: Array.from(channels.values()).sort(compareChannelsByPrice),
        models,
      };
    })
    .filter((m) => m.models.length > 0);
}
